import { useState } from "react";
import { IDataResult, IResult, ResponseStatus } from "../../common/models/Result";
import { useForm } from "../../common/viewmodels/DefaultVM";
import { DataUtils } from "../../common/views/DataUtils";
import { Utils } from "../../common/views/Utils";
import { MenuModel } from "../models/CategoryModel";
import {
    NewGroupModel, createNewGroupModel, NewLineModel,
    NewProductModelWrapper, NewProductModel
} from "../models/NewProductModel";
import { AdminGroupService } from "../services/AdminGroupService";

export default function useNewProductVM() {
    const groupService = new AdminGroupService();

    const {
        form: groupForm,
        setForm: setGroupForm,
        resetForm: resetGroupForm,
        setField: setGroupField,
        errorMessages,
        summaryText,
        summaryType
    } = useForm<NewGroupModel>(createNewGroupModel());

    const [lines, setLines] = useState<NewLineModel[]>([]);
    const [sizes, setSizes] = useState<number[]>([]);
    const [codes, setCodes] = useState<string[]>([]);

    const [submitting, setSubmitting] = useState(false);

    const updateLineField = <K extends keyof NewLineModel>(
        index: number, field: K, value: NewLineModel[K]
    ) => {
        setLines(prev => {
            const newList = [...prev];
            newList[index] = { ...newList[index], [field]: value };
            return newList;
        });
    };

    const updateProductField = <K extends keyof NewProductModel>(
        lineIndex: number, productIndex: number, field: K, value: NewProductModel[K]
    ) => {
        setLines(prev => {
            const newLines = [...prev];
            const newProducts = [...newLines[lineIndex].products];

            newProducts[productIndex] = { ...newProducts[productIndex], [field]: value };
            newLines[lineIndex].products = newProducts;

            return newLines;
        });
    };

    const updateSize = (index: number, value: number) => {
        setSizes(prev => {
            const newList = [...prev];
            newList[index] = value;
            return newList;
        });
    };

    const updateCode = (index: number, value: string) => {
        setCodes(prev => {
            const newList = [...prev];
            newList[index] = value;
            return newList;
        });
    };

    const submit = (categories: MenuModel[]) => {
        if (lines.length === 0) {
            alert("Product lines can't be empty.");
            return;
        }

        for (var l = 0; l < lines.length; l++) {
            if (lines[l].products.length === 0) {
                alert("Products can't be empty.");
                return;
            }
        }

        if (categories) {
            const categoryIds = categories.map((item: MenuModel, index: number) => item.value);
            setGroupField("categoryIds", categoryIds);
        }

        var formData = buildFormData();
        let request = Utils.postFormData(formData, groupService.RoutePostCreate());

        setSubmitting(true);

        DataUtils.onRequest(request, onResult);
    }

    const onResult = (result: IDataResult) => {
        if (!result.errorMessages)
            result.errorMessages = result as IResult;

        setSubmitting(false);

        if (result.status === ResponseStatus.success) {
            const newGroupForm = createNewGroupModel();
            newGroupForm.brandId = groupForm.brandId;
            newGroupForm.categoryIds = groupForm.categoryIds;

            setGroupForm(newGroupForm);
        }
    }

    const buildFormData = () => {
        const model: NewProductModelWrapper = {
            group: groupForm,
            lines: lines,
            sizes: sizes,
            codes: codes
        }

        var requestForm = new FormData();

        const objNames = ["lines", "products"];
        Utils.buildFormDataProperties(requestForm, model, objNames, "");

        return requestForm;
    }

    const generateProducts = () => {
        let lineCount = lines.length;
        let sizeCount = sizes.length;
        let codeCount = codes.length;

        if (lineCount <= 0) return;

        if (sizeCount <= 0) sizeCount = 1;
        if (codeCount <= 0) codeCount = 1;

        const newLines = [];

        for (let l = 0; l < lineCount; l++) {
            let newLine = lines[l];
            newLine.products = [];

            for (let s = 0; s < sizeCount; s++) {
                let size = s < sizes.length ? sizes[s] : undefined;

                for (let c = 0; c < codeCount; c++) {
                    let code = c < codes.length ? codes[c] : undefined;

                    const product: NewProductModel = {
                        fee: groupForm.fee,
                        msrp: groupForm.msrp,
                        price: groupForm.price,
                        shipping: groupForm.shipping,
                        sizeId: size,
                        code: code ?? "",
                        codeOrder: c + 1,
                        barcode: ""
                    };

                    newLine.products.push(product);
                }
            }

            newLines.push(newLine);
        }

        setLines(newLines);
    }

    return {
        groupForm,
        setGroupForm,
        resetGroupForm,
        setGroupField,
        errorMessages,
        summaryText,
        summaryType,

        lines, setLines,
        sizes, setSizes,
        codes, setCodes,

        updateLineField,
        updateProductField,
        updateSize,
        updateCode,

        submitting, setSubmitting,
        generateProducts,
        submit
    }
}